// Tenant-id resolver — used wherever a request needs the caller's tenant_id
// / org_id directly (not just a Bearer token), e.g. the RCS bot backend's
// POST /rcs/bots create payload.
//
// Resolution order:
//   1. Decode the JWT in localStorage.authToken for tenant_id / org_id / tenant
//   2. Otherwise GET /auth/get_userdata — the server's own answer for this
//      login, always asked (not skipped when a cached id exists)
//   3. While that's in flight, use localStorage.linkedin_tenant_id — but only
//      if it was cached for this same user (linkedin_tenant_owner)
//
// The cache used to win outright and was never cleared on logout, so after
// switching account every bot request kept the previous tenant (confirmed:
// fetch-profile went to another tenant's warehouse and served the demo
// profile for a customer that exists in this one).
//
// Returns "" while still resolving; the caller should short-circuit
// its fetches on empty until this settles.

import { useEffect, useState } from "react";

const CACHE_KEY = "linkedin_tenant_id";
const OWNER_KEY = "linkedin_tenant_owner";

export function useTenantId(): string {
  const [tenantId, setTenantId] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = localStorage.getItem("authToken") || "";
      let owner = "";

      // 1. JWT decode
      try {
        const { jwtDecode } = await import("jwt-decode");
        const decoded: any = jwtDecode(token);
        owner = decoded?.sub || decoded?.email || "";
        const fromJwt =
          decoded?.tenant_id ??
          decoded?.tenantId ??
          decoded?.tenant ??
          decoded?.org_id ??
          "";
        if (fromJwt) {
          if (!cancelled) setTenantId(fromJwt);
          localStorage.setItem(CACHE_KEY, fromJwt);
          localStorage.setItem(OWNER_KEY, owner);
          return;
        }
      } catch {
        // JWT malformed or missing — fall through
      }

      // 3 (early). A cached id is only trusted for the user it was cached for.
      const cached = localStorage.getItem(CACHE_KEY) || "";
      if (cached && owner && localStorage.getItem(OWNER_KEY) === owner && !cancelled) setTenantId(cached);

      // 2. Auth service round-trip — always, so a stale cache gets corrected.
      try {
        const authUrl =
          process.env.REACT_APP_AUTH_URL || "https://api.connect.dalmlabs.com";
        const r = await fetch(`${authUrl}/auth/get_userdata`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          const fromApi =
            d?.payload?.org_id ??
            d?.payload?.tenant_id ??
            d?.org_id ??
            d?.tenant_id ??
            "";
          if (fromApi) {
            if (!cancelled) setTenantId(fromApi);
            localStorage.setItem(CACHE_KEY, fromApi);
            localStorage.setItem(OWNER_KEY, owner);
          }
        }
      } catch {
        // Nothing more to try; caller sees empty string and shows an error state
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return tenantId;
}
